import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Icon } from '@/common/components/Icon'
import Loader from '@/common/components/Loader'
import { Header, PageTitle } from '@/components'
import { Button, Card, Checkbox } from '@/components/ui'
import { IColumn, Table } from '@/core/components/Table'
import { useCheckPermission } from '@/core/hooks/use-check-permission'
import { PartnerDto, RoadmapDto, RoadmapUpsertDto } from '@/common/models/gamification'
import {
  createRoadmap,
  deleteRoadmap,
  getPartners,
  getRoadmaps,
  updateRoadmap,
} from '@/core/services/gamification'

import { MessageBox } from '../messages/MessageBox'
import { CreateRoadmapModal } from './CreateRoadmapModal'

/** Prefer the first translation carrying a non-empty title; else fall back to scope. */
const getRoadmapTitle = (row: RoadmapDto): string => {
  const withTitle = row.translations.find((t) => (t.title ?? '').trim() !== '')
  return withTitle?.title ?? `${row.scope} roadmap`
}

export const RoadmapsPage: React.FC = () => {
  const { festivalName, editionId } = useParams()
  const navigate = useNavigate()
  const { hasEditPermission } = useCheckPermission({ page: 'GAMIFICATION' })

  const [roadmaps, setRoadmaps] = useState<RoadmapDto[]>([])
  const [partners, setPartners] = useState<PartnerDto[]>([])
  const [loading, setLoading] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<RoadmapDto | null>(null)

  const [isError, setIsError] = useState(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>()
  const [showMessageBox, setShowMessageBox] = useState(false)
  const [message, setMessage] = useState('')

  const handleError = (e: unknown) => {
    setIsError(true)
    setShowMessageBox(true)
    setMessage(e instanceof Error ? e.message : 'Something went wrong')
    setErrorStatus((e as { response?: { status?: number } })?.response?.status)
  }

  const notify = (msg: string) => {
    setIsError(false)
    setMessage(msg)
    setShowMessageBox(true)
  }

  const getData = useCallback(async () => {
    if (!editionId) return
    try {
      setLoading(true)
      const [{ data: roadmapsData }, { data: partnersData }] = await Promise.all([
        getRoadmaps(editionId),
        getPartners(editionId),
      ])
      setRoadmaps(roadmapsData ?? [])
      setPartners(partnersData ?? [])
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }, [editionId])

  useEffect(() => {
    getData()
  }, [getData])

  const openCreate = () => {
    setEditing(null)
    setCreateOpen(true)
  }

  const openEdit = (row: RoadmapDto) => {
    if (!hasEditPermission) return
    setEditing(row)
    setCreateOpen(true)
  }

  const handleSubmit = async (values: RoadmapUpsertDto, id?: string) => {
    if (!editionId) return
    try {
      setLoading(true)
      if (id) {
        await updateRoadmap(id, values)
        notify('Roadmap updated successfully')
      } else {
        await createRoadmap(editionId, values)
        notify('Roadmap created successfully')
      }
      await getData()
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      setLoading(true)
      await deleteRoadmap(id)
      notify('Roadmap deleted successfully')
      await getData()
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }

  const partnerName = useCallback(
    (partnerId?: string | null) =>
      partners.find((p) => p.id === partnerId)?.name ?? '—',
    [partners],
  )

  const columns: IColumn[] = useMemo(
    () => [
      {
        title: 'Logo',
        accessor: 'logoRef',
        render: (row: RoadmapDto) =>
          row.logoRef ? (
            <img
              src={row.logoRef}
              alt=""
              className="h-[42px] max-w-[84px] rounded-[8px] border border-monochrome-120 object-contain"
            />
          ) : (
            <span className="text-monochrome-2">—</span>
          ),
      },
      {
        title: 'Title',
        accessor: 'title',
        render: (row: RoadmapDto) => (
          <span className="text-monochrome-1">{getRoadmapTitle(row)}</span>
        ),
      },
      {
        title: 'Scope',
        accessor: 'scope',
        render: (row: RoadmapDto) => (
          <span className="text-monochrome-2">{row.scope}</span>
        ),
      },
      {
        title: 'Partner',
        accessor: 'partnerId',
        render: (row: RoadmapDto) => (
          <span className="text-monochrome-2">
            {row.scope === 'PARTNER' ? partnerName(row.partnerId) : '—'}
          </span>
        ),
      },
      {
        title: 'Active',
        accessor: 'active',
        render: (row: RoadmapDto) => (
          <Checkbox checked={row.active} disabled />
        ),
      },
      {
        title: 'Order',
        accessor: 'ordinal',
        render: (row: RoadmapDto) => (
          <span className="text-monochrome-2">{row.ordinal}</span>
        ),
      },
      {
        title: 'Build',
        accessor: '',
        render: (row: RoadmapDto) => (
          <a
            className="view text-accent-1 underline"
            href="#"
            onClick={(e) => {
              e.preventDefault()
              navigate(`/gamification/roadmaps/${festivalName}/${editionId}/${row.id}`)
            }}
          >
            Build
          </a>
        ),
      },
      {
        title: 'Edit',
        accessor: '',
        render: (row: RoadmapDto) => (
          <a
            className="view text-accent-1 underline"
            href="#"
            onClick={(e) => {
              e.preventDefault()
              openEdit(row)
            }}
          >
            Edit
          </a>
        ),
      },
    ],
    [hasEditPermission, partnerName, navigate, festivalName, editionId],
  )

  return (
    <>
      <Header
        pageTitle="Gamification"
        actions={
          hasEditPermission && (
            <Button variant="primary-bordered" onClick={openCreate}>
              Add Roadmap
              <Icon icon="plus-circle" width="18" height="18" viewBox="0 0 18 18" />
            </Button>
          )
        }
      />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle icon="flash" title="Roadmaps" className="mb-5" />
        <Table
          className="[&_th]:!whitespace-nowrap"
          columns={columns}
          data={roadmaps}
          noDataMessage="No roadmaps yet"
        />
      </Card>

      <CreateRoadmapModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        roadmap={editing}
        partners={partners}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
      />

      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
